import { useEffect, useRef, useState } from 'react';
import { Card } from '../game/card';
import { RANKS, SUITS } from '../game/waterfall-poker-game';
import { PlayingCard } from './PlayingCard';

export type CardGridProps = {
    stacks: Card[][];
    deck: Card[];
    cardClicked: (col: number) => void;
    playHand: () => void;
    cardHeightChanged: (height: number) => void;
}

export const MARGIN_SIZE = 8;
// 4 margins
const MARGINS_TTL = 4 * MARGIN_SIZE;
// Card width / height
const CARD_RATIO = 2.5 / 3.5;
const DECK_COLUMN = -1.25;

export function CardGrid({stacks, deck, cardClicked, playHand, cardHeightChanged}: CardGridProps) {
    const [cardHeight, setCardHeight] = useState(0);
    const container = useRef<HTMLDivElement>(null);
    const init = useRef(false);

    const cards = getCardElements();

    useEffect(() => {
        if (container.current) {
            const height = (container.current.clientHeight - MARGINS_TTL) / 5;
            setCardHeight(height);
            cardHeightChanged(height);
        }
        if (!init.current) {
            init.current = true;
            window.addEventListener("keyup", e => {
                if (e.key >= "1" && e.key <= "5") {
                    cardClicked(parseInt(e.key) - 1);
                }
                else if (e.key === "Enter") {
                    playHand()
                }
            });
        }
    }, [setCardHeight, cardHeightChanged, cardClicked, playHand]);

    return (
        <div className='card-grid column' ref={container} style={{width: 5 * CARD_RATIO * (cardHeight + MARGIN_SIZE) - 6}}>
            {cards}
        </div>
    );

    function getCardElements(): JSX.Element[] {
        // console.log("getCardElements");
        const cellHeight = cardHeight + 8;
        const cellWidth = CARD_RATIO * cellHeight;

        const els: JSX.Element[] = [];

        for (const suit of SUITS) {
            for (const rank of RANKS) {
                const pos = findPosition(rank, suit);
                els.push(
                <PlayingCard key={rank + suit[0]} suit={suit} rank={rank}
                    hidden={pos.r === 100}
                    top={((4 - pos.r) * cellHeight) + "px"}
                    left={(pos.c * cellWidth) + "px"}
                    height={cardHeight + "px"}
                    onClick={() => onClick(pos.r, pos.c)}
                />);
            }
        }

        if (deck.length > 0) {
            els.push(<div key="deck" className='deck' style={{top: 0, left: DECK_COLUMN * cellWidth, width: cellWidth, height: cardHeight}}></div>);
        }

        return els;
    }

    function findPosition(rank: string, suit: string): { r: number; c: number; } {
        // This causes it to be too slow
        // // Look for the card in the deck
        // if (deck.findIndex(card => card.rank === rank && card.suit === suit) >= 0) {
        //     // Move to deck area
        //     return { r: 4, c: DECK_COLUMN };
        // }

        // Look through the stacks for the card
        for (let r = 0; r < 5; r++) {
            for (let c = 0; c < 5; c++) {
                const card = stacks[c][r];
                if (card && card.rank === rank && card.suit === suit) {
                    return { r, c };
                }
            }
        }

        // Out of play
        return { r: 100, c: 100 };
    }
    
    function onClick(row: number, col: number): void {
        if (row === 0) {
            cardClicked(col);
        }
    }
}

